(() => {
  if (window.VitaBlueShipping) return;

  let configuration;
  let context;
  let state;
  let revision = 0;
  const rates = new Map();
  const contextSelector = '[data-vb-shipping-context]';

  const readContext = (root = document) => {
    const node = root.querySelector(contextSelector);
    return node ? JSON.parse(node.textContent) : null;
  };

  const getConfiguration = () => {
    if (!configuration) {
      const url = document.querySelector('vitablue-shipping-progress, vitablue-shipping-ticker')?.dataset.configUrl;
      configuration = fetch(url, { signal: AbortSignal.timeout(8000) })
        .then((response) => {
          if (!response.ok) throw new Error('Shipping configuration unavailable');
          return response.json();
        })
        .catch((error) => {
          configuration = undefined;
          throw error;
        });
    }
    return configuration;
  };

  const rateFor = (currency, country, config) => {
    if (currency === config.baseCurrency) return Promise.resolve(1);
    const current = window.Shopify?.currency;
    if (window.Shopify?.country === country && current?.active === currency &&
        Number.isFinite(Number(current.rate)) && Number(current.rate) > 0) {
      return Promise.resolve(Number(current.rate));
    }
    const key = `${country}:${currency}`;
    if (!rates.has(key)) {
      const url = new URL(window.Shopify?.routes?.root || '/', location.origin);
      url.searchParams.set('country', country);
      rates.set(key, fetch(url, { credentials: 'omit', signal: AbortSignal.timeout(8000) })
        .then((response) => {
          if (!response.ok) throw new Error('Currency conversion unavailable');
          return response.text();
        })
        .then((html) => {
          const selected = html.match(/Shopify\.country\s*=\s*"([A-Z]{2})"/)?.[1];
          const value = html.match(/Shopify\.currency\s*=\s*(\{[^{}]*\})/)?.[1];
          const converted = value && JSON.parse(value);
          const rate = Number(converted?.rate);
          if (selected !== country || converted?.active !== currency || !Number.isFinite(rate) || rate <= 0) {
            throw new Error('Unverified currency conversion');
          }
          return rate;
        })
        .catch((error) => {
          rates.delete(key);
          throw error;
        }));
    }
    return rates.get(key);
  };

  const formatAmount = (amount, selection, trim = false) => {
    const formatter = new Intl.NumberFormat(selection.locale, {
      style: 'currency', currency: selection.currency, ...(trim ? { minimumFractionDigits: 0 } : {}),
    });
    const unit = 10 ** Math.max(0, 2 - formatter.resolvedOptions().maximumFractionDigits);
    return formatter.format(Math.ceil(amount / unit) * unit / 100);
  };

  const resolveMarket = async (selection, profileIds) => {
    try {
      const config = await getConfiguration();
      if (!config.markets.some((market) => market.countries.includes(selection.country) &&
          (!selection.market || market.id === String(selection.market)))) throw new Error('Unknown market');
      const ids = profileIds || [config.storefrontProfile];
      if (ids.length !== 1) throw new Error('Unverified shipping profile');
      const profile = config.profiles.find((profile) => profile.id === ids[0]);
      if (!profile) throw new Error('Unknown shipping profile');
      const rule = profile.rules.find((rule) => rule.countries.includes(selection.country)) ||
        profile.rules.find((rule) => rule.countries.includes('*'));
      if (!rule) throw new Error('No destination shipping rule');
      let threshold = rule.threshold;
      if (rule.currency !== selection.currency) {
        const sourceCountry = config.currencyCountries[rule.currency];
        if (!sourceCountry) throw new Error('No verified currency reference');
        const [source, target] = await Promise.all([
          rateFor(rule.currency, sourceCountry, config), rateFor(selection.currency, selection.country, config),
        ]);
        threshold = Math.ceil(rule.threshold * target / source);
        const rounding = profile.conversionRounding?.[selection.country];
        if (rounding?.currency === selection.currency) {
          if (!Number.isSafeInteger(rounding.threshold) || rounding.threshold < 0 ||
              !Number.isSafeInteger(rounding.increment) || rounding.increment <= 0 ||
              rounding.threshold % rounding.increment !== 0) throw new Error('Invalid threshold rounding');
          threshold = Math.max(rounding.threshold, Math.ceil(threshold / rounding.increment) * rounding.increment);
        }
      }
      if (!Number.isSafeInteger(threshold) || threshold < 0) throw new Error('Invalid threshold');
      return {
        ...selection, status: 'ready', threshold,
        formattedThreshold: formatAmount(threshold, selection, true),
      };
    } catch {
      return { ...selection, status: 'unavailable', threshold: null, formattedThreshold: null };
    }
  };

  const resolve = async (cart) => {
    const unavailable = (reason) => ({
      ...cart, status: 'unavailable', reason, threshold: null,
      remaining: null, progress: 0, unlocked: false,
    });
    if (!cart || !Number.isSafeInteger(cart.subtotal) || cart.subtotal < 0 || !Array.isArray(cart.items)) {
      return unavailable('invalid-cart');
    }
    try {
      const config = await getConfiguration();
      if (!config.markets.some((market) => market.countries.includes(cart.country) &&
          (!cart.market || market.id === String(cart.market)))) return unavailable('unknown-market');
      const profiles = new Set();
      for (const item of cart.items.filter((item) => item.requires_shipping !== false)) {
        const profile = config.profiles.find((profile) => profile.products.includes(String(item.product_id)));
        if (!profile) return unavailable('unknown-profile');
        profiles.add(profile);
      }
      if (!profiles.size) return unavailable('no-shippable-items');
      if (profiles.size > 1) return unavailable('mixed-shipping-profiles');
      const market = await resolveMarket(cart, [...profiles].map((profile) => profile.id));
      if (market.status !== 'ready') return unavailable('unverified-shipping-rule');
      const { threshold, formattedThreshold } = market;
      const remaining = Math.max(0, threshold - cart.subtotal);
      return {
        ...cart, status: 'ready', threshold, formattedThreshold, remaining, unlocked: remaining === 0,
        progress: threshold ? Math.min(1, cart.subtotal / threshold) : 1,
      };
    } catch {
      return unavailable('unverified-shipping-rule');
    }
  };

  const update = async (cart) => {
    const current = ++revision;
    context = cart;
    state = Object.freeze({ ...cart, status: 'pending', unlocked: false, threshold: null, remaining: null, progress: 0 });
    window.dispatchEvent(new CustomEvent('vitablue:shipping-update', { detail: state }));
    const result = await resolve(cart);
    if (current !== revision) return state;
    state = Object.freeze(result);
    window.dispatchEvent(new CustomEvent('vitablue:shipping-update', { detail: state }));
    return state;
  };

  const refresh = async (cart) => {
    const current = revision;
    const snapshot = readContext() || context;
    if (!snapshot) return update(null);
    if (!Number.isSafeInteger(cart?.total_price) || !Array.isArray(cart?.items)) {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { cache: 'no-store' });
      if (!response.ok) return update(null);
      cart = await response.json();
      if (current !== revision) return state;
    }
    if (cart.currency !== snapshot.currency) return update(null);
    return update({ ...snapshot, subtotal: cart.total_price, items: cart.items });
  };

  window.VitaBlueShipping = Object.freeze({ resolve, resolveMarket, refresh, getState: () => state, getConfiguration });

  customElements.define('vitablue-shipping-ticker', class extends HTMLElement {
    connectedCallback() {
      this.onState = (event) => {
        const result = event.detail;
        if (result.status === 'ready' && result.country === this.dataset.country &&
            result.currency === this.dataset.currency && String(result.market) === this.dataset.market) {
          this.render(result);
        }
      };
      window.addEventListener('vitablue:shipping-update', this.onState);
      this.observer = new MutationObserver(() => this.sync());
      this.observer.observe(this, {
        attributes: true, attributeFilter: ['data-country', 'data-market', 'data-currency', 'data-locale'],
      });
      this.sync();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      window.removeEventListener('vitablue:shipping-update', this.onState);
      this.revision = (this.revision || 0) + 1;
    }

    async sync() {
      const current = this.revision = (this.revision || 0) + 1;
      const { country, market, currency, locale } = this.dataset;
      this.render({ status: 'pending' });
      const result = await resolveMarket({ country, market, currency, locale });
      if (current === this.revision) this.render(result);
    }

    render(result) {
      this.querySelectorAll('[data-vb-shipping-message]').forEach((item) => {
        item.hidden = result.status !== 'ready';
        if (!item.hidden) item.textContent = `FREE SHIPPING OVER ${result.formattedThreshold}`;
      });
    }
  });

  if (typeof PerformanceObserver !== 'undefined') {
    let queued = false;
    let running = false;
    const observer = new PerformanceObserver((records) => {
      if (!records.getEntries().some((entry) => {
        const url = new URL(entry.name);
        return url.origin === location.origin && /\/cart\/(add|change|update|clear)(\.js)?$/.test(url.pathname);
      })) return;
      queued = true;
      if (running) return;
      running = true;
      queueMicrotask(async () => {
        while (queued) {
          queued = false;
          await refresh().catch(() => update(null));
        }
        running = false;
      });
    });
    observer.observe({ type: 'resource' });
  }

  customElements.define('vitablue-shipping-progress', class extends HTMLElement {
    connectedCallback() {
      this.onState = (event) => this.render(event.detail);
      window.addEventListener('vitablue:shipping-update', this.onState);
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        const section = event.cartData?.sections?.['cart-drawer'];
        const snapshot = section && readContext(new DOMParser().parseFromString(section, 'text/html'));
        if (snapshot) update(snapshot);
        else refresh(event.cartData).catch(() => update(null));
      });
      this.observer = new MutationObserver(() => this.sync());
      this.observer.observe(this.closest('cart-drawer'), { childList: true, subtree: true, characterData: true });
      this.sync();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.unsubscribe?.();
      window.removeEventListener('vitablue:shipping-update', this.onState);
    }

    sync() {
      const node = this.closest('cart-drawer').querySelector(contextSelector);
      if (!node || node.textContent === this.snapshot) return;
      this.snapshot = node.textContent;
      update(JSON.parse(this.snapshot));
    }

    render(result) {
      this.hidden = result.status !== 'ready';
      if (this.hidden) return;
      const amount = formatAmount(result.remaining, result);
      const text = result.unlocked ? this.dataset.unlocked :
        (result.progress >= 0.9 ? this.dataset.close : this.dataset.away).replace('[amount]', amount);
      this.querySelector('.vb-shipping__message').textContent = text;
      this.querySelector('.vb-shipping__fill').style.width = `${result.progress * 100}%`;
    }
  });
})();
